import { Chess } from "chess.js";
import { extractAnnotationsFromComment } from "./Annotation";
import { NAG_VALUES, convertNumericNagToSymbolic } from "./Nag";
import { PgnData } from "./PgnData";
import { PgnMove } from "./PgnMove";
import { PgnParser } from "./PgnParser";
export class PgnDataBuilder {
    pgn;
    chess;
    _pgnData = null;
    _validationErrors = [];
    constructor(pgn, chess = null) {
        if (!pgn || typeof pgn !== "string") {
            throw new Error("Invalid PGN format: PGN must be a non-empty string");
        }
        this.pgn = PgnParser.normalize(pgn.trim());
        this.chess = chess || new Chess();
        if (!PgnParser.isValidPgn(this.pgn)) {
            throw new Error("Invalid PGN format: PGN must contain moves or a valid FEN");
        }
        // Check for malformed headers
        if (PgnParser.hasMalformedHeaders(this.pgn)) {
            this._validationErrors.push("PGN contains malformed headers (missing closing brackets or quotes)");
        }
    }
    get validationErrors() {
        return this._validationErrors;
    }
    get hasInvalidMoves() {
        return this._validationErrors.length > 0;
    }
    pgnData() {
        if (this._pgnData)
            return this._pgnData;
        this._pgnData = PgnData.create(this.pgn, this.chess, this);
        return this._pgnData;
    }
    parseMoves() {
        const moves = [];
        let currentMove = null;
        const variationStack = [];
        const initialFen = PgnParser.extractInitialFen(this.pgn);
        const chess = this.chess;
        chess.load(initialFen);
        // Always create a preMove to represent the starting position
        const preMove = PgnMove.createPreMove(initialFen);
        const tokens = PgnParser.tokenizeMoves(this.pgn);
        let moveNumber = 1;
        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            if (token === "(") {
                if (!currentMove)
                    continue;
                variationStack.push({
                    moves: [],
                    parentMove: currentMove,
                });
                // load fen before the current move
                chess.undo();
                currentMove = currentMove.previousMove;
                continue;
            }
            if (token === ")") {
                const variation = variationStack.pop();
                if (!variation)
                    continue;
                // Restore position from before variation using FEN (much faster than load_pgn)
                currentMove = variation.parentMove;
                chess.load(currentMove.fen);
                // Restore move number after exiting variation:
                // If parent was White, next Black move has the same move number
                // If parent was Black, next White move is number + 1
                moveNumber =
                    currentMove.color === "w"
                        ? currentMove.number
                        : currentMove.number + 1;
                continue;
            }
            // Skip move numbers and move number continuations
            if (PgnParser.REGEX.MOVE_NUMBER.test(token)) {
                moveNumber = parseInt(token.match(PgnParser.REGEX.MOVE_NUMBER_EXTRACT)[0]);
                continue;
            }
            if (token === "*") {
                // unfinished game
                continue;
            }
            // Skip game result markers (1-0, 0-1, 1/2-1/2, *)
            if (PgnParser.REGEX.GAME_RESULT.test(token)) {
                continue;
            }
            // if token match user score, set currentMove userScore
            if (PgnParser.REGEX.USER_SCORE.test(token)) {
                if (currentMove) {
                    const score = parseInt(token.match(PgnParser.REGEX.USER_SCORE_EXTRACT)[1]);
                    currentMove.loadUserScore(score);
                }
                continue;
            }
            if (PgnParser.REGEX.COMMENT.test(token)) {
                // Extract annotations from comment and get cleaned comment text
                // extractAnnotationsFromComment handles removing curly braces internally
                const { shapes, mcq, puzzleAnnotation, orient: commentOrient, cleanComment, } = extractAnnotationsFromComment(token);
                // Attach orient to the current move (or preMove if before first move)
                if (commentOrient) {
                    if (currentMove) {
                        currentMove.updateOrient(commentOrient);
                    }
                    else {
                        preMove.updateOrient(commentOrient);
                    }
                }
                // Check if this is a puzzle marker
                // Format: {[#]}, {[# attempts="2"]}, {[# mate_in="2" ai_level="4"]}
                if (puzzleAnnotation) {
                    // Attach puzzleAnnotation to the anchor move (the move just before {[#]},
                    // or preMove if no moves have been played yet)
                    const anchorMove = currentMove || preMove;
                    anchorMove.updatePuzzleAnnotation(puzzleAnnotation);
                    // Continue to next token after processing puzzle marker
                    continue;
                }
                // Add shapes to current move if any were found
                if (currentMove && shapes.length > 0) {
                    currentMove.updateShapes(shapes);
                }
                // Add MCQ to current move if found
                if (currentMove && mcq) {
                    currentMove.updateMcq(mcq);
                }
                // Handle pre-game shapes, MCQ, and comments (before first move)
                if (!currentMove) {
                    if (shapes.length > 0) {
                        if (!preMove.shapes) {
                            preMove.shapes = [];
                        }
                        preMove.shapes.push(...shapes);
                    }
                    if (mcq) {
                        preMove.updateMcq(mcq);
                    }
                    if (cleanComment) {
                        if (preMove.comment) {
                            preMove.updateComment(`${preMove.comment}\n${cleanComment}`);
                        }
                        else {
                            preMove.updateComment(cleanComment);
                        }
                    }
                }
                else if (cleanComment) {
                    // Add comment to current move
                    if (currentMove.comment) {
                        currentMove.updateComment(`${currentMove.comment}\n${cleanComment}`);
                    }
                    else {
                        currentMove.updateComment(cleanComment);
                    }
                }
                continue;
            }
            // Check if token is a NAG
            if (NAG_VALUES.includes(token)) {
                if (currentMove) {
                    currentMove.updateNag(token);
                }
                continue;
            }
            // Check if token is a numeric NAG (e.g., $1, $16)
            if (PgnParser.REGEX.NAG_ANNOTATION.test(token)) {
                if (currentMove) {
                    // Convert numeric NAG to symbolic equivalent
                    const symbolicNag = convertNumericNagToSymbolic(token);
                    if (symbolicNag && NAG_VALUES.includes(symbolicNag)) {
                        currentMove.updateNag(symbolicNag);
                    }
                }
                continue;
            }
            if (token.trim() === "") {
                continue;
            }
            const variation = variationStack.length > 0
                ? variationStack[variationStack.length - 1]
                : null;
            const move = new PgnMove(token, chess, currentMove, variation && variation.moves.length === 0 ? variation.parentMove : null, initialFen);
            if (!move.id) {
                // Invalid move detected - add error message
                this._validationErrors.push(`Invalid chess move: "${token}"`);
                continue;
            }
            if (move.number !== moveNumber) {
                const errorMsg = `Move number mismatch: expected ${moveNumber}, got ${move.number} for move "${move.san}"`;
                console.error(errorMsg);
                this._validationErrors.push(errorMsg);
            }
            if (variation) {
                variation.moves.push(move);
            }
            else {
                moves.push(move);
            }
            // Check if the token contains a NAG by comparing with the move's SAN
            if (token !== move.san && token.startsWith(move.san)) {
                const possibleNag = token.slice(move.san.length);
                if (NAG_VALUES.includes(possibleNag)) {
                    move.updateNag(possibleNag);
                }
            }
            currentMove = move;
        }
        // Link preMove to rootMove for navigation
        const rootMove = moves.length > 0 ? moves[0] : null;
        if (rootMove) {
            preMove.nextMove = rootMove;
            // Back-link root move to preMove so puzzleRole chain can reach the anchor
            rootMove.setPreMove(preMove);
        }
        return {
            rootMove,
            preMove,
        };
    }
}
